import React, { useState } from "react";

import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { formatBytes, formatNumber } from "utils/format";
import { useRoutes } from "utils/routes";

import Panel from "components/Panel";
import PanelSection from "components/PanelSection";
import Loading from "components/Loading";
import Grid from "components/Grid";

import QUERY from "./Query.graphql";

import {
  QueryDetailIndexAdvisorInfo,
  QueryDetailIndexAdvisorInfoVariables,
} from "./types/QueryDetailIndexAdvisorInfo";

import { getScanMethodInfo } from "./util";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { ParameterizedScanInfo } from "components/IndexSelectionResults/ScanDetails";
import { Issue } from "types/types";

type IssueType = Pick<Issue, "id" | "checkGroupAndName" | "groupingKey">;

type Props = {
  databaseId: string;
  queryId: string;
  issues: IssueType[];
};

const QueryDetailsIndexAdvisorV2: React.FunctionComponent<Props> = ({
  databaseId,
  queryId,
  issues,
}) => {
  const { data, loading, error } = useQuery<
    QueryDetailIndexAdvisorInfo,
    QueryDetailIndexAdvisorInfoVariables
  >(QUERY, {
    variables: {
      databaseId,
      queryId,
    },
  });
  const [includePartitions, setIncludePartitions] = useState(false);

  const secondaryTitle = (
    <label className="!px-0">
      <input
        type="checkbox"
        checked={includePartitions}
        id="include_partitions"
        onChange={(e) => setIncludePartitions(e.target.checked)}
      />{" "}
      Show table partitions
    </label>
  );

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <Panel title="Scans" secondaryTitle={secondaryTitle}>
      <ScanGrid
        databaseId={databaseId}
        scans={data.getSchemaTableScansForQuery}
        includePartitions={includePartitions}
        issues={issues}
      />
    </Panel>
  );
};

type ScanRowData = {
  databaseId: string;
  scanId: string;
  fullTableName: string;
  table: {
    id: string;
    schemaName: string;
    tableName: string;
  };
  tableSize: number | null;
  scanMethod: string;
  totalCost: number;
  whereExpression: string;
  joinExpression: string;
  suggestedIndexIssueId: string | null;
  suggestedIndexDescription: string | null;
  parameterizedScanExpected: boolean;
};

const TableScanCell: React.FunctionComponent<{
  rowData: ScanRowData;
}> = ({ rowData }) => {
  const { databaseTable, databaseTableIndexSelection, databaseIssue } =
    useRoutes();
  const [scanMethodIcon, scanMethodClassname] = getScanMethodInfo({
    scanMethod: rowData.scanMethod,
    joinExp: rowData.joinExpression,
    whereExp: rowData.whereExpression,
  });
  return (
    <div className="m-1">
      <Link to={databaseTable(rowData.databaseId, rowData.table.id)}>
        {rowData.table.schemaName}.{rowData.table.tableName}
      </Link>
      <div className="ml-2 mb-1 grid grid-cols-[120px_minmax(0,1fr)] gap-1 mt-1.5">
        <div className="text-[#777777] font-medium">WHERE clause</div>
        <div className="font-mono whitespace-normal">
          {rowData.whereExpression || "-"}
        </div>
        <div className="text-[#777777] font-medium">JOIN clause</div>
        <div>
          {rowData.joinExpression ? (
            <div>
              <div className="font-mono whitespace-normal mb-1">
                {rowData.joinExpression}
              </div>
              <ParameterizedScanInfo
                parameterizedScanExpected={rowData.parameterizedScanExpected}
              />
            </div>
          ) : (
            "-"
          )}
        </div>
        <div className="text-[#777777] font-medium">Scan method</div>
        <div className="whitespace-normal">
          <FontAwesomeIcon
            className={scanMethodClassname}
            icon={scanMethodIcon}
          />{" "}
          {rowData.scanMethod}
        </div>

        <div className="text-[#777777] font-medium">Index Advisor</div>
        <div className="truncate">
          {rowData.suggestedIndexIssueId ? (
            <Link
              to={
                databaseIssue(
                  rowData.databaseId,
                  rowData.suggestedIndexIssueId,
                  "index_advisor/indexing_engine",
                ) +
                "#S-" +
                rowData.scanId
              }
            >
              <FontAwesomeIcon className="inline-block" icon={faCircleInfo} />{" "}
              Create new index on {rowData.suggestedIndexDescription}
            </Link>
          ) : (
            <Link
              to={databaseTableIndexSelection(
                rowData.databaseId,
                rowData.table.id,
              )}
            >
              <FontAwesomeIcon
                className="text-[#43962A] inline-block"
                icon={faCheckCircle}
              />{" "}
              No recommended changes
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const ScanGrid: React.FunctionComponent<{
  databaseId: string;
  scans: QueryDetailIndexAdvisorInfo["getSchemaTableScansForQuery"];
  includePartitions: boolean;
  issues: IssueType[];
}> = ({ databaseId, scans, includePartitions, issues }) => {
  if (scans.length === 0) {
    // TODO: this may indicate an error in Index Advisor analysis, but right now we can't distinguish
    // that from the case of a query not scanning any tables: e.g., SELECT * FROM udf()
    return <PanelSection>No scans found in this query.</PanelSection>;
  }
  const scanData = scans
    .filter((scan) => {
      return includePartitions || !scan.table.parentTableId;
    })
    .map((scan) => {
      return {
        databaseId,
        scanId: scan.id,
        fullTableName: scan.table.schemaName + "." + scan.table.tableName,
        table: scan.table,
        tableSize: scan.table.lastStats?.dataSizeBytes,
        scanMethod: scan.genericPlanScanMethod,
        totalCost: scan.genericPlanTotalCost,
        whereExpression: scan.whereExpression,
        joinExpression: scan.joinExpression,
        suggestedIndexIssueId: scan.suggestedIndex
          ? issues.find(
              (issue) =>
                issue.checkGroupAndName == "index_advisor/indexing_engine" &&
                String(issue.groupingKey["table"]) == scan.table.id,
            )?.id
          : null,
        suggestedIndexDescription: scan.suggestedIndex,
        parameterizedScanExpected: scan.parameterizedScanExpected,
      };
    });

  return (
    <Grid
      data={scanData}
      defaultSortBy="totalCost"
      columns={[
        {
          field: "fullTableName",
          header: "Table and Scan Details",
          width: "4fr",
          renderer: TableScanCell,
        },
        {
          field: "totalCost",
          header: "Cost",
          width: "1fr",
          style: "number",
          defaultSortOrder: "DESC",
          renderer: ({ fieldData }) => formatNumber(fieldData, 2),
        },
        {
          field: "tableSize",
          header: "Table Size",
          width: "1fr",
          style: "number",
          nullValue: "n/a",
          defaultSortOrder: "DESC",
          renderer: function TableSizeCell({ fieldData }) {
            return formatBytes(fieldData);
          },
        },
      ]}
    />
  );
};

export default QueryDetailsIndexAdvisorV2;
